import React, { createContext, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { datePopperConfig } from "../Shared/datePopperConfig.js";
import AddItemCustomFields from "../Shared/AddItemCustomFields.js";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import EntityAssociation from "../TaskManagerApp/EntityAssociation.js";

const AddTaskContext = createContext(null);

const recurrences = [
    { id: 0, name: "Does not repeat" },
    { id: 1, name: "Weekly" },
    { id: 2, name: "Monthly" },
    { id: 3, name: "Yearly" },
];

const toLink = (entityType, entityId, name, url) => ({
    EntityType: entityType,
    EntityId: entityId == null ? "" : String(entityId),
    EntityName: name || "",
    EntityUrl: url || "",
    RelationshipId: 0,
    TaskId: 0,
    Deleted: 0,
});

const fetchEntity = (entityType, entityId) => {
    return fetch(`/umbraco/api/TaskManagerApi/Entity?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`)
        .then((response) => response.ok ? response.json() : null)
        .then((data) => data && data.Name != null
            ? toLink(data.EntityType || entityType, data.EntityId || entityId, data.Name, data.Url)
            : null)
        .catch(() => null);
};

const buildLinkedEntities = async (globalContext) => {
    const linked = [];
    const push = (entry) => {
        if (!entry || !entry.EntityType)
            return;
        if (linked.some((item) => item.EntityType === entry.EntityType && String(item.EntityId) === String(entry.EntityId)))
            return;
        linked.push(entry);
    };

    for (const item of globalContext.externalLinkedObjects || []) {
        if (item.EntityType === "systematic-review-item") {
            const parts = String(item.EntityId || "").split(".");
            const reviewId = parts[0];
            const itemId = parts.length > 1 ? `${parts[0]}.${parts[1]}` : item.EntityId;
            // Prefer the host page entity type (appraisal card / SR viewer). Fall back to iframe parent when used that way.
            const parentType = (window.parent && window.parent !== window)
                ? window.parent.currentEntityType
                : window.currentEntityType;
            if (parentType && reviewId)
                push(await fetchEntity(parentType, reviewId));
            push(await fetchEntity("systematic-review-item", itemId) || toLink(item.EntityType, itemId));
        } else {
            push(await fetchEntity(item.EntityType, item.EntityId) || toLink(item.EntityType, item.EntityId, item.EntityName || item.Name));
        }
    }

    const scopeType = globalContext.linkedEntityType;
    const scopeId = globalContext.linkedEntityId;
    if (scopeType || scopeId) {
        const resolved = scopeId ? await fetchEntity(scopeType, scopeId) : null;
        push(resolved || toLink(scopeType, scopeId));
    }

    return linked;
};

const formatDate = (date) => {
    if (!date)
        return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const AddItemPopup = ({ globalContext }) => {
    const isSubtask = !!globalContext.addTaskParentTaskId;
    const parentName =
        globalContext.currentItem?.Id == globalContext.addTaskParentTaskId
            ? globalContext.currentItem?.Name
            : (globalContext.data || []).find((item) => item.Id == globalContext.addTaskParentTaskId)?.Name;

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [assignedTo, setAssignedTo] = useState(window.currentUserId || "");
    const [startDate, setStartDate] = useState(null);
    const [dueDate, setDueDate] = useState(null);
    const [notifyUser, setNotifyUser] = useState(true);
    const [recurrency, setRecurrency] = useState(0);
    const [recurrencyEndDate, setRecurrencyEndDate] = useState(null);
    const [linkedObjectsList, setLinkedObjectsList] = useState([]);
    const [entityTypes, setEntityTypes] = useState([]);
    const [isEntityAssociationExpanded, setIsEntityAssociationExpanded] = useState(false);
    const [errorName, setErrorName] = useState(false);
    const [errorStartDate, setErrorStartDate] = useState(false);
    const [errorDueDate, setErrorDueDate] = useState(false);
    const [errorRecurrencyEndDate, setErrorRecurrencyEndDate] = useState(false);
    const [error, setError] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const source = globalContext?.pendingCreate;
        if (source) {
            if (source.taskTitle)
                setName(source.taskTitle);
            if (source.taskDescription)
                setDescription(source.taskDescription);
            if (source.taskStartDate)
                setStartDate(new Date(source.taskStartDate));
            if (source.userId)
                setAssignedTo(source.userId);
        }

        fetch("/umbraco/api/TaskManagerApi/Menu?entityType=&entityId=")
            .then((response) => response.ok ? response.json() : null)
            .then((data) => setEntityTypes((data?.Nodes || []).filter((entry) => entry.IsSearchable)))
            .catch(() => { });

        buildLinkedEntities(globalContext)
            .then((links) => setLinkedObjectsList(links))
            .catch(() => { });
    }, []);

    const close = () => {
        globalContext.setAddTaskParentTaskId?.(null);
        if (globalContext.closeAllPopups)
            globalContext.closeAllPopups();
        else if (globalContext.setIsVisibleAddItemPopup)
            globalContext.setIsVisibleAddItemPopup(false);
    };

    const save = () => {
        let valid = true;
        setError("");
        setErrorName(false);
        setErrorStartDate(false);
        setErrorDueDate(false);
        setErrorRecurrencyEndDate(false);

        if (!name) {
            setErrorName(true);
            valid = false;
        }
        if (!startDate) {
            setErrorStartDate(true);
            valid = false;
        }
        if (!dueDate) {
            setErrorDueDate(true);
            valid = false;
        }
        if (Number(recurrency) !== 0 && !recurrencyEndDate) {
            setErrorRecurrencyEndDate(true);
            valid = false;
        }
        if (!valid)
            return;

        setIsSaving(true);
        const body = {
            Id: 0,
            Name: name,
            Description: description,
            Priority: 4,
            StartDate: formatDate(startDate),
            DueDate: formatDate(dueDate),
            EstimatedTime: 0,
            AssignedTo: Number(assignedTo),
            CreatedBy: window.currentUserId,
            CompletionStatus: 0,
            CompanyId: window.currentCompanyId,
            Board: null,
            BoardStep: null,
            ParentTaskId: globalContext.addTaskParentTaskId || null,
            NotifyUser: notifyUser,
            Recurrency: Number(recurrency),
            RecurrencyEndDate: Number(recurrency) !== 0 && recurrencyEndDate ? formatDate(recurrencyEndDate) : null,
            Relationship_Task_Entity: linkedObjectsList,
            CustomFieldsAnswers: globalContext.newItemCustomFieldsAnswers || [],
        };

        fetch("/umbraco/api/TaskManagerApi/TaskList", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        })
            .then(async (response) => {
                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(text || "Could not create the task");
                }
                return response.json();
            })
            .then((result) => {
                const parentId = globalContext.addTaskParentTaskId;
                close();

                if (parentId && result) {
                    // Keep parent SubtaskCount and expanded-child cache in sync
                    if (globalContext.setCurrentItem && globalContext.currentItem?.Id == parentId) {
                        globalContext.setCurrentItem({
                            ...globalContext.currentItem,
                            SubtaskCount: (globalContext.currentItem.SubtaskCount || 0) + 1,
                        });
                    }
                    if (globalContext.setData) {
                        globalContext.setData((prev) => (prev || []).map((item) =>
                            item.Id == parentId
                                ? { ...item, SubtaskCount: (item.SubtaskCount || 0) + 1 }
                                : item
                        ));
                    }
                    if (globalContext.setChildRowsByParent) {
                        globalContext.setChildRowsByParent((prev) => {
                            const existing = prev[parentId];
                            if (!existing)
                                return prev;
                            if (existing.some((child) => child.Id === result.Id))
                                return prev;
                            return { ...prev, [parentId]: [...existing, result] };
                        });
                    }
                    if (globalContext.selectedReferences?.includes(parentId) && globalContext.setSelectedReferences) {
                        globalContext.setSelectedReferences((prev) =>
                            prev.includes(result.Id) ? prev : [...prev, result.Id]
                        );
                    }
                    // Refresh the Sub Tasks tab list if the parent details pane is open
                    globalContext.reloadSubtasks?.();
                } else {
                    globalContext.refreshData?.();
                    globalContext.loadData?.();
                }
            })
            .catch((err) => setError(err.message || "Could not create the task"))
            .finally(() => setIsSaving(false));
    };

    const associationContext = {
        linkedObjectsList,
        setLinkedObjectsList,
        addTaskEntityTypesList: entityTypes,
        useDummy: false,
        clearEntitySearchResults: 0,
        getStaticEntityTypeName: (type) => {
            if (type === "systematic-review-item") return "Systematic Review Items";
            return type;
        },
    };

    return (
        <AddTaskContext.Provider value={associationContext}>
            <div className="add-task-popup" style={globalContext.hasLimitedWidth ? { width: "70%" } : null}>
                <div style={{ display: "flex", flexDirection: "row", marginBottom: "5px" }}>
                    <div style={{ width: "50%" }}>
                        <h3>{isSubtask ? "Add subtask" : "Add task"}</h3>
                    </div>
                    <div style={{ width: "50%", textAlign: "right" }}>
                        <button className="close-button" onClick={close}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                </div>

                {isSubtask && (
                    <>
                        <br />
                        <div style={{ fontStyle: "italic", width: "100%" }}>
                            <span style={{ fontWeight: "bold" }}>Main task:</span> {parentName || "Current task"}
                        </div>
                        <br />
                    </>
                )}

                {error && <div className="form-field-error-message" style={{ marginBottom: "10px" }}>{error}</div>}

                <input
                    value={name}
                    className={`form-field-text add-task-field-name ${errorName ? "form-field-error" : ""}`}
                    onChange={(e) => setName(e.target.value)}
                    style={{ width: "100%" }}
                    type="text"
                    placeholder={isSubtask ? "Subtask name" : "Task name"}
                />
                {errorName && <span className="form-field-error-message">This field is necessary</span>}
                <br />
                <br />
                <textarea
                    value={description}
                    className="form-field-text add-task-field-description"
                    style={{ width: "100%" }}
                    placeholder={isSubtask ? "Subtask description" : "Task description"}
                    onChange={(e) => setDescription(e.target.value)}
                />
                <br />
                <br />

                <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
                    <div style={{ width: "33%" }}>
                        Assign to
                        <br />
                        <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                            {(globalContext.users || []).map((user) => (
                                <option key={user.Id} value={user.Id}>{user.Name}</option>
                            ))}
                        </select>
                        <div style={{ width: "100%", display: "flex", flexDirection: "row", marginTop: "10px" }}>
                            <label>
                                <input type="checkbox" checked={notifyUser} onChange={() => setNotifyUser(!notifyUser)} /> Notify user by email
                            </label>
                        </div>
                    </div>
                    <div style={{ width: "33%" }}>
                        Start Date
                        <br />
                        <DatePicker
                            className={`datepicker-field add-task-field-date-start ${errorStartDate ? "form-field-error" : ""}`}
                            dateFormat="yyyy-MM-dd"
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            popperPlacement={datePopperConfig.placement}
                            popperModifiers={datePopperConfig.modifiers}
                        />
                        {errorStartDate && (
                            <div style={{ width: "100%" }}>
                                <span className="form-field-error-message">This field is necessary</span>
                            </div>
                        )}
                    </div>
                    <div style={{ width: "33%" }}>
                        Due Date
                        <br />
                        <DatePicker
                            className={`datepicker-field add-task-field-date-due ${errorDueDate ? "form-field-error" : ""}`}
                            dateFormat="yyyy-MM-dd"
                            selected={dueDate}
                            onChange={(date) => setDueDate(date)}
                            popperPlacement={datePopperConfig.placement}
                            popperModifiers={datePopperConfig.modifiers}
                        />
                        {errorDueDate && (
                            <div style={{ width: "100%" }}>
                                <span className="form-field-error-message">This field is necessary</span>
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ marginTop: "20px", width: "100%", display: "flex", flexDirection: "row" }}>
                    <div style={{ width: "33%" }}>
                        Does this task repeat? <i className="fas fa-info-circle g-ml-5 cursor-helper" style={{ color: "#999" }} title="This feature will automatically create separate tasks that repeat periodically until the selected end date"></i>
                        <br />
                        <select onChange={(e) => setRecurrency(e.target.value)} value={recurrency}>
                            {recurrences.map((item) => (
                                <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ width: "33%" }}>
                        {Number(recurrency) !== 0 && (
                            <>
                                When should it stop repeating?
                                <br />
                                <DatePicker
                                    className={`datepicker-field ${errorRecurrencyEndDate ? "form-field-error" : ""}`}
                                    dateFormat="yyyy-MM-dd"
                                    selected={recurrencyEndDate}
                                    onChange={(date) => setRecurrencyEndDate(date)}
                                    popperPlacement={datePopperConfig.placement}
                                    popperModifiers={datePopperConfig.modifiers}
                                />
                                {errorRecurrencyEndDate && (
                                    <div style={{ width: "100%" }}>
                                        <span className="form-field-error-message">This field is necessary</span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <br />
                <AddItemCustomFields entityType={globalContext.entityTypeId} context={globalContext} />

                <div style={{ textAlign: "center", display: "flex", flexDirection: "row", width: "100%" }}>
                    <div
                        onClick={() => setIsEntityAssociationExpanded(!isEntityAssociationExpanded)}
                        style={{ padding: "10px 16px", width: "100%", fontWeight: 500, color: "#333", backgroundColor: "#fff", marginBottom: "30px", border: "1px solid #dcdcdc", borderRadius: "6px", cursor: "pointer" }}
                    >
                        <i className={isEntityAssociationExpanded ? "fas fa-angle-up" : "fas fa-angle-down"} style={{ fontSize: "16px" }}></i>
                        <span style={{ marginLeft: "20px" }}>Linked Items</span>
                    </div>
                </div>
                {isEntityAssociationExpanded && <EntityAssociation context={AddTaskContext} />}

                <div className="action-button-bar">
                    <div className="action-button-bar-left">
                        <button className="action-button cancel-button" onClick={close}>Cancel</button>
                    </div>
                    <div className="action-button-bar-center"></div>
                    <div className="action-button-bar-right">
                        {isSaving ? <LoadingSpinnerImage /> : (
                            <button className="action-button save-button" onClick={save}>
                                {isSubtask ? "Add subtask" : "Add task"}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </AddTaskContext.Provider>
    );
};

export default AddItemPopup;
